import { useRef, useState, useEffect, useContext } from "react";
import readNDJSONStream from "ndjson-readablestream";
import { throttle } from "lodash";
import { Spinner, ISpinnerStyles } from "@fluentui/react";

import { chatApi, ChatRequest, generateHistory, HistoryRequest, AssistantsLoadingState, ChatResponse, Message, MessageContext } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AppStateContext } from "../../state/AppProvider";
import { Disclaimer } from "../../components/Disclaimer";
import { MobileSidebar } from "../../components/MobileSidebar";
import { DefaultSidebar } from "../../components/DefaultSidebar";
import { ChatHeader } from "../../components/ChatHeader";
import { ButtonTooltip } from "../../components/ButtonTooltip";

import reggieNoDisc from "../../assets/Reggie-Icon-NoDisc.svg";

import { ChatHistoryLoadingState, CosmosDBStatus } from "../../api";
import { Conversation } from "../../api";

const Chat = () => {
    let userErrorMessage: string = "Something went wrong. If this issue persists, please contact reggiefeedback@jerseyfsc.org.";

    const [iframeKey, setIframeKey] = useState(0);

    const [conversationId, setConversationId] = useState<string>();
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [isProfanity, setIsProfanity] = useState<boolean>(false);
    const [isMessageLimit, setIsMessageLimit] = useState<boolean>(false);
    const [isConversationLimit, setIsConversationLimit] = useState<boolean>(false);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeCitationTitle, setActiveCitationTitle] = useState<string>();
    const [showThoughts, setShowThoughts] = useState<boolean[]>([]);
    const [commentInputs, setCommentInputs] = useState<string[]>([]);
    const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);
    const [isAutoScroll, setIsAutoScroll] = useState<boolean>(true);

    const [answers, setAnswers] = useState<[user: string, response: ChatResponse][]>([]);
    const [streamedAnswers, setstreamedAnswers] = useState<[user: string, response: ChatResponse][]>([]);

    const appStateContext = useContext(AppStateContext);

    const handleAsyncRequest = async (question: string, answers: [string, ChatResponse][], setAnswers: Function, responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatResponse = {} as ChatResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatResponse = {
                        ...askResponse,
                        choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
                    };
                    setstreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (
                    event["choices"] &&
                    event["choices"][0]["context"] &&
                    event["choices"][0]["context"]["metadata"] &&
                    event["choices"][0]["context"]["metadata"]["thoughts"]
                ) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = event as ChatResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    // Update context with new keys from latest event
                    askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                } else if (event["flagged"]) {
                    setIsProfanity(true);
                    throw Error(event["flagged"]);
                } else if (event["message_limit"]) {
                    setIsMessageLimit(true);
                    throw Error(event["message_limit"]);
                } else if (event["conversation_limit"]) {
                    setIsConversationLimit(true);
                    throw Error(event["conversation_limit"]);
                } else if (event["expired"]) {
                    setIsExpired(true);
                    throw Error(event["expired"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatResponse = {
            ...askResponse,
            choices: [{ ...askResponse.choices[0], message: { content: answer, role: askResponse.choices[0].message.role } }]
        };
        return fullResponse;
    };

    //const client = useMsal().instance;

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsProfanity(false);
        setIsMessageLimit(false);
        setIsConversationLimit(false);
        setIsExpired(false);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);
        setShowThoughts(currentShowThoughts => [...currentShowThoughts, false]);
        setCommentInputs(currentCommentInputs => [...currentCommentInputs, ""]);
        appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });

        const token = sessionStorage.getItem("token");

        try {
            const messages: Message[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            let userContext: MessageContext = {
                chat_status: answers.length ? answers[answers.length - 1][1].choices[0].context.chat_status : null
            };

            const request: ChatRequest = {
                assistant: appStateContext?.state.assistant?.assistant || "",
                messages: [...messages, { content: question, role: "user" }],
                context: userContext,
                stream: true,
                // ChatAppProtocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };

            const response = await chatApi(request, token || undefined);
            if (!response.body) {
                throw Error("No response body");
            }
            const parsedResponse: ChatResponse = await handleAsyncRequest(question, answers, setAnswers, response.body);
            if (parsedResponse.choices[0].message.content === "") {
                throw Error(userErrorMessage);
            }

            setAnswers((prevAnswers: [user: string, response: ChatResponse][]) => {
                const updatedAnswers: [user: string, response: ChatResponse][] = [...prevAnswers, [question, parsedResponse]];

                const messages: Message[] = updatedAnswers.flatMap(a => [
                    { content: a[0], role: "user" },
                    { content: a[1].choices[0].message.content, role: "assistant" }
                ]);

                const historyRequest: HistoryRequest = {
                    conversation_id: conversationId,
                    assistant: appStateContext?.state.assistant?.assistant || "",
                    messages: [...messages],
                    context: parsedResponse.choices[0].context,
                    user_context: userContext
                };

                generateHistory(historyRequest)
                    .then(histResponse => {
                        // Ensure the response is OK and parse it as JSON
                        if (!histResponse.ok) {
                            throw Error("Network response was not ok");
                        }
                        return histResponse.json();
                    })
                    .then(data => {
                        let resultConversation: Conversation | undefined;
                        if (!data.conversation) {
                            throw Error("Invalid response body");
                        }
                        if (!conversationId) {
                            resultConversation = {
                                id: data.conversation.id,
                                assistant: data.conversation.assistant,
                                title: data.conversation.title,
                                messages: data.messages,
                                date: data.conversation.date
                            };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: resultConversation });
                            setConversationId(data.conversation.conversation_id);
                        } else {
                            resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId);
                            if (!resultConversation) {
                                throw Error("Invalid conversation id");
                            }
                            resultConversation.messages.push(data.messages[0]);
                            resultConversation.messages.push(data.messages[1]);
                            resultConversation.date = data.conversation.date;
                            let updatedConversation = { ...resultConversation };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: updatedConversation });
                        }
                        return resultConversation;
                    })
                    .then(resultConversation => {
                        if (resultConversation) {
                            appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: resultConversation });
                        }
                    })
                    .catch(e => {
                        appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                        appStateContext?.dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                        console.error(e);
                    });

                return updatedAnswers; // return updated state
            });
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
            appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });
        }
    };

    const clearChat = () => {
        if (!appStateContext?.state.answerLoading) {
            lastQuestionRef.current = "";
            error && setError(undefined);
            setIsProfanity(false);
            setIsMessageLimit(false);
            setIsConversationLimit(false);
            setIsExpired(false);
            setActiveCitation(undefined);
            setActiveCitationTitle(undefined);
            setShowThoughts([]);
            setCommentInputs([]);
            setAnswers([]);
            setstreamedAnswers([]);
            setConversationId(undefined);
            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
            if (windowWidth < 639 && appStateContext?.state.isChatHistoryOpen) {
                appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
            }
        }
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    useEffect(() => {
        if (isAutoScroll) {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        }
    }, [streamedAnswers]);

    const scrollToEnd = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleScroll = () => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainer;
            const isScrolledToBottom = Math.ceil(scrollTop + clientHeight) + 1 >= scrollHeight;
            setIsAutoScroll(isScrolledToBottom);
        }
    };

    useEffect(() => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, [lastQuestionRef.current]);

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, title: string, index: number) => {
        if (windowWidth < 1280) {
            window.open(citation, "_blank");
        } else {
            setActiveCitation(citation);
            setActiveCitationTitle(title);
            setIframeKey(prevKey => prevKey + 1);
        }
    };

    const onCloseCitation = () => {
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);
    };

    const onSetCommentInput = (comment: string, index: number) => {
        setCommentInputs(currentShowThoughts => {
            const updatedCommentInputs = [...currentShowThoughts];
            updatedCommentInputs[index] = comment;
            return updatedCommentInputs;
        });
    };

    const handleHistoryClick = () => {
        appStateContext?.state.isChatHistoryOpen
            ? document.documentElement.style.setProperty("--ch", `${0}px`)
            : document.documentElement.style.setProperty("--ch", `${300}px`);
        appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
    };

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const vh = window.innerHeight;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
        document.documentElement.style.setProperty("--ch", `${0}px`);
    }, []);

    useEffect(() => {
        // The function won't be called more often than every 200ms
        const handleResize = throttle(() => {
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            setWindowHeight(vh);
            setWindowWidth(vw);
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        }, 200);

        window.addEventListener("resize", handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (appStateContext?.state.currentChat && appStateContext?.state.currentChat.id !== conversationId) {
            error && setError(undefined);
            setIsProfanity(false);
            setIsMessageLimit(false);
            setIsConversationLimit(false);
            setIsExpired(false);
            setActiveCitation(undefined);

            const updatedShowThoughts: boolean[] = [];
            const updatedCommentInputs: string[] = [];
            const transformedAnswers: [string, ChatResponse][] = [];
            const messages = appStateContext.state.currentChat.messages;

            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    transformedAnswers.push([
                        messages[i].message.content as string,
                        {
                            choices: [
                                {
                                    index: i,
                                    message: messages[i + 1].message,
                                    context: messages[i + 1].context,
                                    session_state: null,
                                    message_id: messages[i + 1].id,
                                    feedback: messages[i + 1].feedback,
                                    feedback_comment: messages[i + 1].feedback_comment
                                }
                            ]
                        } as ChatResponse
                    ]);
                    updatedShowThoughts.push(false);
                    updatedCommentInputs.push("");
                }
            }
            if (messages[messages.length - 1].message.role === "user") {
                // Final assistant message is missing
                setError(userErrorMessage);
            }

            setShowThoughts(updatedShowThoughts);
            setCommentInputs(updatedCommentInputs);
            setAnswers(transformedAnswers);
            setstreamedAnswers(transformedAnswers);

            setConversationId(appStateContext.state.currentChat.id);

            if (appStateContext.state.assistants) {
                const currentAssistant = appStateContext.state.assistants?.find(assist => assist.assistant === appStateContext.state.currentChat?.assistant);
                appStateContext?.dispatch({ type: "UPDATE_ASSISTANT", payload: currentAssistant || null });
            }

            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].message.role === "user") {
                    lastQuestionRef.current = messages[i].message.content as string;
                    break;
                }
            }
        } else if (appStateContext?.state.currentChat) {
            const transformedAnswers: [string, ChatResponse][] = [];
            const messages = appStateContext.state.currentChat.messages;
            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    transformedAnswers.push([
                        messages[i].message.content as string,
                        {
                            choices: [
                                {
                                    index: i,
                                    message: messages[i + 1].message,
                                    context: messages[i + 1].context,
                                    session_state: null,
                                    message_id: messages[i + 1].id,
                                    feedback: messages[i + 1].feedback,
                                    feedback_comment: messages[i + 1].feedback_comment
                                }
                            ]
                        } as ChatResponse
                    ]);
                }
            }
            setAnswers(transformedAnswers);
            setstreamedAnswers(transformedAnswers);
        } else {
            clearChat;
        }
    }, [appStateContext?.state.currentChat]);

    function copyToClipboard(text: string) {
        let updatedText = text.replace(/\.\w+#page=(\d+)/g, "");
        updatedText = updatedText.replace(/#page_label=/g, " - Page ");
        if (navigator.clipboard) {
            navigator.clipboard.writeText(updatedText).catch(err => {
                console.error("Failed to copy text using Clipboard API: ", err);
                // Try the fallback method
                fallbackCopyTextToClipboard(updatedText);
            });
        } else {
            fallbackCopyTextToClipboard(updatedText);
        }
    }

    function fallbackCopyTextToClipboard(text: string) {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            // Using the Clipboard API again for the fallback method
            navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Fallback failed too: ", err);
        }
        document.body.removeChild(textArea);
    }

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#139699 #13969930 #13969930",
            height: "16px",
            width: "16px"
        }
    };

    return (
        <div className="relative z-0 flex h-full w-full overflow-hidden">
            <Disclaimer open={showDisclaimer} setOpen={b => setShowDisclaimer(b)} />
            <div className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden">
                <ChatHeader
                    onNewClick={clearChat}
                    newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                    onHistoryClick={handleHistoryClick}
                    windowWidth={windowWidth}
                />
                <main
                    className={
                        "relative h-full w-full flex-1 overflow-auto transition-width "
                        //  + (activeCitation ? "xl:flex " : "")
                        // + (appStateContext?.state.isChatHistoryOpen ? "xl:pl-6" : "")
                    }
                >
                    <div role="presentation" tabIndex={0} className="flex h-full flex-col focus-visible:outline-0">
                        {/*  + (activeCitation ? "xl:w-1/2" : "")}> */}
                        <div className="flex-1 overflow-hidden">
                            {appStateContext?.state.assistant &&
                                (!lastQuestionRef.current ? (
                                    <div className="relative h-full">
                                        <div className="flex h-full flex-col items-center justify-center">
                                            <img src={reggieNoDisc} alt="Reggie" width="160px" height="160px" />
                                            <div className="text-j-green text-4xl mt-3 text-center">Ask Reggie a question</div>
                                            <div className="text-j-dark text-2xl mt-3 text-center">Or try an example</div>
                                            <div className="mx-4 mt-6 flex max-w-3xl flex-wrap items-stretch justify-center gap-4">
                                                <ExampleList onExampleClicked={onExampleClicked} />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={"relative h-full " + (activeCitation ? "flex" : "")}>
                                        <div className="h-full overflow-y-auto w-full" ref={chatScrollRef}>
                                            <div className="p-0">
                                                <div className="flex flex-col">
                                                    {isStreaming &&
                                                        streamedAnswers.map((streamedAnswer, index) => (
                                                            <div key={index} className={"" + (index === 0 ? "" : "")}>
                                                                <UserChatMessage message={streamedAnswer[0]} />
                                                                <Answer
                                                                    isStreaming={true}
                                                                    key={index}
                                                                    answer={streamedAnswer[1]}
                                                                    onCitationClicked={(filePath, citationTitle) =>
                                                                        onShowCitation(filePath, citationTitle, index)
                                                                    }
                                                                    onCopyAnswerClicked={() => copyToClipboard(streamedAnswer[1].choices[0].message.content)}
                                                                    lastAnswer={index === answers.length}
                                                                    commentInput={commentInputs[index]}
                                                                    setCommentInput={commentInput => onSetCommentInput(commentInput, index)}
                                                                    windowWidth={windowWidth}
                                                                />
                                                            </div>
                                                        ))}
                                                    {!isStreaming &&
                                                        answers.map((answer, index) => (
                                                            <div key={index} className={"" + (index === 0 ? "" : "")}>
                                                                <UserChatMessage message={answer[0]} />
                                                                <Answer
                                                                    isStreaming={false}
                                                                    key={index}
                                                                    answer={answer[1]}
                                                                    onCitationClicked={(filePath, citationTitle) =>
                                                                        onShowCitation(filePath, citationTitle, index)
                                                                    }
                                                                    onCopyAnswerClicked={() => copyToClipboard(answer[1].choices[0].message.content)}
                                                                    lastAnswer={index === answers.length}
                                                                    commentInput={commentInputs[index]}
                                                                    setCommentInput={commentInput => onSetCommentInput(commentInput, index)}
                                                                    windowWidth={windowWidth}
                                                                />
                                                            </div>
                                                        ))}
                                                    {isLoading && (
                                                        <div className={"" + (answers.length === 0 ? "" : "")}>
                                                            <UserChatMessage message={lastQuestionRef.current} />
                                                            <AnswerLoading />
                                                        </div>
                                                    )}
                                                    {error ? (
                                                        <div className={"" + (answers.length === 0 ? "" : "")}>
                                                            <UserChatMessage message={lastQuestionRef.current} />
                                                            <AnswerError
                                                                error={error.toString()}
                                                                onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                                                isProfanity={isProfanity}
                                                                isConversationLimit={isConversationLimit}
                                                                isMessageLimit={isMessageLimit}
                                                                isExpired={isExpired}
                                                            />
                                                        </div>
                                                    ) : null}
                                                    <div ref={chatMessageStreamEnd} />
                                                </div>
                                            </div>
                                        </div>
                                        {activeCitation && windowWidth >= 1280 && (
                                            <div className="h-full overflow-y-auto w-full flex">
                                                <div className="flex-1 overflow-y-auto my-4 mx-4 hidden w-1/2 xl:flex">
                                                    <div className="w-full px-0 justify-center md:gap-6 m-auto lg:max-w-3xl">
                                                        <div className="relative flex w-full flex-col">
                                                            <div className="flex justify-between">
                                                                <div className="flex grow justify-start max-w-[calc(100%-4rem)]">
                                                                    <div className="leading-7 font-semibold text-[0.875rem] mt-0.5 mb-2.5 text-ellipsis overflow-x-hidden whitespace-nowrap">
                                                                        {activeCitationTitle}
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-end">
                                                                    <ButtonTooltip title="Hide source" placement="top">
                                                                        <span className="">
                                                                            <a
                                                                                className="h-10 rounded-lg px-1 text-j-green cursor-pointer"
                                                                                onClick={onCloseCitation}
                                                                            >
                                                                                <span className="h-[24px] w-[24px] shrink-0 text-2xl material-symbols-outlined">
                                                                                    visibility_off
                                                                                </span>
                                                                            </a>
                                                                        </span>
                                                                    </ButtonTooltip>
                                                                </div>
                                                            </div>
                                                            <iframe
                                                                key={iframeKey}
                                                                title="Citation"
                                                                src={activeCitation}
                                                                width="100%"
                                                                height={windowHeight - 309 + "px"}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                        {appStateContext?.state.assistant && (
                            <div className="w-full py-6 bg-j-light-green">
                                <QuestionInput
                                    clearOnSend
                                    placeholder="Message Reggie..."
                                    disabled={appStateContext?.state.answerLoading}
                                    onSend={question => makeApiRequest(question)}
                                    showToEnd={!isAutoScroll && lastQuestionRef.current !== ""}
                                    toEnd={scrollToEnd}
                                    activeCitation={activeCitation}
                                    windowWidth={windowWidth}
                                />
                            </div>
                        )}
                        {appStateContext?.state.assistantsLoadingState === AssistantsLoadingState.Loading && (
                            <div className="relative h-full">
                                <div className="flex h-full flex-col items-center justify-center">
                                    <a className="gap-1 w-fit font-bold flex text-center items-center text-lg whitespace-nowrap">
                                        <span className="flex justify-center items-center h-[26.67px] w-[26.67px]">
                                            <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                        </span>
                                        <span className="text-[0.875rem]">
                                            <span className="">Loading</span>
                                        </span>
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
            {windowWidth >= 768 && (
                <DefaultSidebar
                    onNewClick={clearChat}
                    newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                    onHistoryClick={handleHistoryClick}
                />
            )}
            {windowWidth < 768 && (
                <MobileSidebar
                    onNewClick={clearChat}
                    newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                    onHistoryClick={handleHistoryClick}
                />
            )}
        </div>
    );
};

export default Chat;
